import { useEffect, useState } from "react";
import { api, formatApiError, openSecureFile } from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { toast } from "sonner";
import { FileText, Plus, Pencil, Trash2, Download, Inbox, X, Send, CheckCircle2, Clock, AlertTriangle, CalendarClock, Undo2 } from "lucide-react";

const TIPI = [["testo", "Testo"], ["testolungo", "Testo lungo"], ["data", "Data"], ["numero", "Numero"], ["scelta", "Scelta"]];
const EMPTY = { titolo: "", descrizione: "", campi: [], attivo: true };

export const ModuliStudio = () => {
  const [moduli, setModuli] = useState(null);
  const [compilazioni, setCompilazioni] = useState([]);
  const [editing, setEditing] = useState(null);
  // Iter122k41 · Dialog risposta ad una compilazione (crea un avviso)
  const [replyTo, setReplyTo] = useState(null); // {compilazione, titolo, testo, priorita, sending}
  // Iter122k68 · filtro aperti/tutti + dialog evasa
  const [filtroAperti, setFiltroAperti] = useState(true);
  const [evasaFor, setEvasaFor] = useState(null); // { compilazione, note, sending }

  const load = () => {
    api.get("/moduli").then((r) => setModuli(r.data.moduli)).catch(() => setModuli([]));
    api.get("/moduli/compilazioni").then((r) => setCompilazioni(r.data.compilazioni)).catch(() => {});
  };
  useEffect(load, []);

  const save = async () => {
    const m = editing;
    if (!m.titolo.trim()) { toast.error("Indica il titolo del modulo"); return; }
    if (!m.campi.some((c) => c.etichetta.trim())) { toast.error("Aggiungi almeno un campo"); return; }
    const payload = { titolo: m.titolo, descrizione: m.descrizione, attivo: m.attivo, campi: m.campi };
    try {
      if (m.id) await api.put(`/moduli/${m.id}`, payload);
      else await api.post("/moduli", payload);
      toast.success("Modulo salvato"); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); }
  };

  const toggleAttivo = async (m) => {
    try { await api.put(`/moduli/${m.id}`, { titolo: m.titolo, descrizione: m.descrizione, campi: m.campi, attivo: !m.attivo }); load(); }
    catch { toast.error("Errore"); }
  };

  const remove = async (m) => {
    if (!window.confirm(`Eliminare il modulo "${m.titolo}"?`)) return;
    try { await api.delete(`/moduli/${m.id}`); load(); } catch { toast.error("Errore"); }
  };

  const setCampo = (i, patch) => setEditing((e) => ({ ...e, campi: e.campi.map((c, j) => (j === i ? { ...c, ...patch } : c)) }));

  // Iter122k41 · Rispondi ad una compilazione: crea un avviso Bacheca al condòmino
  const openReply = (c) => {
    setReplyTo({
      compilazione: c,
      titolo: `Risposta al tuo modulo: ${c.modulo_titolo}`,
      testo: "",
      priorita: "Info",
      sending: false,
    });
  };
  const inviaRisposta = async () => {
    if (!replyTo) return;
    const titolo = (replyTo.titolo || "").trim();
    const testo = (replyTo.testo || "").trim();
    if (!titolo) { toast.error("Inserisci il titolo della risposta"); return; }
    if (!testo) { toast.error("Scrivi il testo della risposta"); return; }
    setReplyTo((r) => ({ ...r, sending: true }));
    try {
      await api.post(`/moduli/compilazioni/${replyTo.compilazione.id}/rispondi`,
        { titolo, testo, priorita: replyTo.priorita });
      toast.success(`Risposta inviata a ${replyTo.compilazione.user_nome} e salvata in Bacheca.`);
      setReplyTo(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Errore invio");
      setReplyTo((r) => ({ ...r, sending: false }));
    }
  };

  // Iter122k68 · toggle evasa (senza dialog nel caso "riapri")
  const toggleEvasa = async (c, evasa, note = "") => {
    try {
      await api.post(`/moduli/compilazioni/${c.id}/evasa`, { evasa, note });
      toast.success(evasa ? "Attività segnata come evasa." : "Attività riaperta.");
      setEvasaFor(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Errore aggiornamento stato");
    }
  };
  const confermaEvasa = async () => {
    if (!evasaFor) return;
    setEvasaFor((r) => ({ ...r, sending: true }));
    await toggleEvasa(evasaFor.compilazione, true, (evasaFor.note || "").trim());
  };

  return (
    <div className="space-y-5" data-testid="moduli-studio">
      <Card className="p-4 border-2">
        <div className="flex items-center justify-between gap-2 mb-3">
          <h2 className="font-display text-xl font-bold text-brand flex items-center gap-2"><FileText className="w-5 h-5" /> Moduli per i condòmini</h2>
          <Button data-testid="modulo-nuovo" size="sm" className="h-9 bg-brand hover:bg-brand-dark" onClick={() => setEditing({ ...EMPTY, campi: [{ etichetta: "", tipo: "testo", obbligatorio: false, opzioni: [] }] })}>
            <Plus className="w-4 h-4 mr-1" /> Nuovo modulo
          </Button>
        </div>
        <p className="text-sm text-slate-500 mb-3">I moduli attivi sono compilabili dai condòmini nella loro sezione "Documenti e moduli". Ogni compilazione arriva allo Studio via email e resta archiviata qui sotto.</p>
        {!moduli && <div className="text-slate-500">Caricamento…</div>}
        <div className="space-y-2">
          {(moduli || []).map((m) => (
            <div key={m.id} className="p-3 rounded-lg border border-slate-200 flex flex-wrap items-center gap-2" data-testid={`modulo-row-${m.id}`}>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-sm break-words">{m.titolo}</div>
                <div className="text-xs text-slate-500">{m.campi.length} campi</div>
              </div>
              <label className="flex items-center gap-1.5 text-xs font-semibold cursor-pointer">
                <input type="checkbox" className="w-4 h-4 accent-brand" checked={m.attivo} onChange={() => toggleAttivo(m)} data-testid={`modulo-attivo-${m.id}`} />
                Attivo
              </label>
              <Button size="sm" variant="outline" className="h-8 px-2 text-xs" onClick={() => setEditing({ ...m, campi: m.campi.map((c) => ({ ...c })) })} data-testid={`modulo-edit-${m.id}`}>
                <Pencil className="w-4 h-4" />
              </Button>
              <Button size="sm" variant="outline" className="h-8 px-2 text-xs text-red-700" onClick={() => remove(m)} data-testid={`modulo-del-${m.id}`}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-4 border-2" data-testid="compilazioni-card">
        {(() => {
          const aperti = compilazioni.filter((c) => !c.evasa);
          const inRit = aperti.filter((c) => c.in_ritardo).length;
          const filtered = filtroAperti ? aperti : compilazioni;
          return (
            <>
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <h2 className="font-display text-xl font-bold text-brand flex items-center gap-2">
                  <Inbox className="w-5 h-5" /> Compilazioni ricevute ({compilazioni.length})
                </h2>
                {aperti.length > 0 && (
                  <span data-testid="compilazioni-badge-aperti"
                    className="text-xs font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300">
                    {aperti.length} da evadere
                  </span>
                )}
                {inRit > 0 && (
                  <span data-testid="compilazioni-badge-ritardo"
                    className="text-xs font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-800 border border-red-300 inline-flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> {inRit} in ritardo
                  </span>
                )}
                <div className="ml-auto flex items-center gap-1 text-xs">
                  <button type="button" data-testid="compilazioni-filtro-aperti"
                    onClick={() => setFiltroAperti(true)}
                    className={`px-2 py-1 rounded-full border ${filtroAperti ? "bg-brand text-white border-brand" : "bg-white border-slate-300 hover:bg-slate-50"}`}>
                    Solo aperti
                  </button>
                  <button type="button" data-testid="compilazioni-filtro-tutti"
                    onClick={() => setFiltroAperti(false)}
                    className={`px-2 py-1 rounded-full border ${!filtroAperti ? "bg-brand text-white border-brand" : "bg-white border-slate-300 hover:bg-slate-50"}`}>
                    Tutti
                  </button>
                </div>
              </div>
              {filtered.length === 0 ? (
                <div className="text-slate-500 text-sm">
                  {filtroAperti ? "Nessuna attività aperta." : "Nessun modulo compilato dai condòmini finora."}
                </div>
              ) : (
                <div className="space-y-2">
                  {filtered.map((c) => {
                    const gg = c.giorni_alla_scadenza;
                    const stateBadge = c.evasa
                      ? { cls: "bg-emerald-100 text-emerald-800 border-emerald-300", label: `Evasa ${c.evasa_at ? "il " + new Date(c.evasa_at).toLocaleDateString("it-IT") : ""}`, Icon: CheckCircle2 }
                      : c.in_ritardo
                        ? { cls: "bg-red-100 text-red-800 border-red-300", label: `In ritardo ${gg != null ? `(${Math.abs(gg)}gg)` : ""}`, Icon: AlertTriangle }
                        : gg != null && gg <= 1
                          ? { cls: "bg-amber-100 text-amber-800 border-amber-300", label: gg === 0 ? "Scade OGGI" : "Scade domani", Icon: Clock }
                          : { cls: "bg-slate-100 text-slate-700 border-slate-300", label: c.scadenza_evasione ? `Scad. ${c.scadenza_evasione}` : "Aperta", Icon: CalendarClock };
                    const SIcon = stateBadge.Icon;
                    return (
                      <div key={c.id} className="p-3 rounded-lg border border-slate-200 flex flex-wrap items-center gap-2" data-testid={`compilazione-${c.id}`}>
                        <div className="min-w-0 flex-1">
                          <div className="font-semibold text-sm break-words flex items-center gap-2">
                            {c.modulo_titolo}
                            <span data-testid={`compilazione-stato-${c.id}`}
                              className={`text-[10px] font-bold px-2 py-0.5 rounded-full border inline-flex items-center gap-1 ${stateBadge.cls}`}>
                              <SIcon className="w-3 h-3" /> {stateBadge.label}
                            </span>
                          </div>
                          <div className="text-xs text-slate-500">{c.user_nome} · {c.condominio_nome} · {new Date(c.created_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}</div>
                          {c.evasa_note && (
                            <div className="text-[11px] text-emerald-700 mt-0.5" data-testid={`compilazione-evasa-note-${c.id}`}>Note: {c.evasa_note}</div>
                          )}
                          {c.risposto_at && (
                            <div className="text-[11px] text-emerald-700 font-semibold flex items-center gap-1 mt-0.5" data-testid={`compilazione-risposto-${c.id}`}>
                              <CheckCircle2 className="w-3.5 h-3.5" /> Risposto il {new Date(c.risposto_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                            </div>
                          )}
                        </div>
                        <Button size="sm" variant="outline" className="h-8 px-2 text-xs" data-testid={`compilazione-pdf-${c.id}`}
                          onClick={() => openSecureFile(`/moduli/compilazioni/${c.id}/pdf?inline=true`)}>
                          <Download className="w-4 h-4 mr-1" /> PDF
                        </Button>
                        {/* Iter122k41 · Rispondi: crea un avviso Bacheca al condòmino */}
                        <Button size="sm"
                          data-testid={`compilazione-rispondi-${c.id}`}
                          onClick={() => openReply(c)}
                          className={`h-8 px-2 text-xs font-bold ${c.risposto_at ? "bg-slate-100 text-slate-700 hover:bg-slate-200" : "bg-brand text-white hover:bg-brand-dark"}`}>
                          <Send className="w-4 h-4 mr-1" /> {c.risposto_at ? "Rispondi di nuovo" : "Rispondi"}
                        </Button>
                        {/* Iter122k68 · Evasa toggle */}
                        {c.evasa ? (
                          <Button size="sm" variant="outline"
                            data-testid={`compilazione-riapri-${c.id}`}
                            onClick={() => toggleEvasa(c, false)}
                            className="h-8 px-2 text-xs border-amber-400 text-amber-800 hover:bg-amber-50">
                            <Undo2 className="w-4 h-4 mr-1" /> Riapri
                          </Button>
                        ) : (
                          <Button size="sm"
                            data-testid={`compilazione-evasa-${c.id}`}
                            onClick={() => setEvasaFor({ compilazione: c, note: "", sending: false })}
                            className="h-8 px-2 text-xs bg-emerald-700 text-white hover:bg-emerald-800">
                            <CheckCircle2 className="w-4 h-4 mr-1" /> Segna evasa
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          );
        })()}
      </Card>

      {/* Iter122k41 · Dialog Rispondi ad una compilazione (crea un avviso Bacheca) */}
      <Dialog open={!!replyTo} onOpenChange={(o) => !o && !replyTo?.sending && setReplyTo(null)}>
        <DialogContent aria-describedby={undefined} className="max-w-lg" data-testid="dialog-rispondi-compilazione">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2"><Send className="w-5 h-5" /> Rispondi al condòmino</DialogTitle>
          </DialogHeader>
          {replyTo && (
            <div className="space-y-3">
              <div className="text-xs text-slate-600 p-2 rounded bg-slate-50 border border-slate-200">
                <div>Destinatario: <b>{replyTo.compilazione.user_nome}</b> ({replyTo.compilazione.condominio_nome})</div>
                <div>Modulo: <b>{replyTo.compilazione.modulo_titolo}</b></div>
                <div className="text-[11px] text-slate-500 mt-1">La risposta viene salvata come <b>avviso di Bacheca</b> e inviata via email in modalità <b>no-reply</b> dal mittente di sistema.</div>
              </div>
              <div>
                <Label className="text-sm font-semibold">Priorità</Label>
                <select className="w-full h-10 mt-1 border-2 border-slate-300 rounded-lg px-2 bg-white text-sm"
                  data-testid="reply-priorita"
                  value={replyTo.priorita}
                  onChange={(e) => setReplyTo({ ...replyTo, priorita: e.target.value })}>
                  <option value="Info">Info</option>
                  <option value="Importante">Importante</option>
                  <option value="Urgente">Urgente</option>
                </select>
              </div>
              <div>
                <Label className="text-sm font-semibold">Titolo</Label>
                <Input data-testid="reply-titolo" className="h-11 mt-1"
                  value={replyTo.titolo}
                  onChange={(e) => setReplyTo({ ...replyTo, titolo: e.target.value })} />
              </div>
              <div>
                <Label className="text-sm font-semibold">Messaggio</Label>
                <textarea data-testid="reply-testo" rows={6}
                  className="w-full mt-1 border-2 border-slate-300 rounded-lg px-3 py-2 text-sm"
                  value={replyTo.testo}
                  onChange={(e) => setReplyTo({ ...replyTo, testo: e.target.value })} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" data-testid="reply-annulla" onClick={() => setReplyTo(null)} disabled={replyTo?.sending}>Annulla</Button>
            <Button data-testid="reply-invia" onClick={inviaRisposta} disabled={replyTo?.sending}
              className="bg-brand hover:bg-brand-dark">
              <Send className="w-4 h-4 mr-1" /> {replyTo?.sending ? "Invio…" : "Invia risposta"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent aria-describedby={undefined} className="max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-xl">{editing?.id ? "Modifica modulo" : "Nuovo modulo"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <Label className="text-sm font-semibold">Titolo</Label>
                <Input data-testid="modulo-titolo" className="h-11 mt-1" value={editing.titolo} onChange={(e) => setEditing({ ...editing, titolo: e.target.value })} />
              </div>
              <div>
                <Label className="text-sm font-semibold">Descrizione / dichiarazioni (compare sul modulo e sul PDF)</Label>
                <textarea data-testid="modulo-descrizione" rows={3} className="w-full mt-1 border-2 border-slate-300 rounded-lg px-3 py-2 text-sm"
                  value={editing.descrizione} onChange={(e) => setEditing({ ...editing, descrizione: e.target.value })} />
              </div>
              <div>
                <Label className="text-sm font-semibold">Campi</Label>
                <div className="space-y-2 mt-1">
                  {editing.campi.map((c, i) => (
                    <div key={i} className="p-2 rounded-lg border border-slate-200 flex flex-wrap items-center gap-2">
                      <Input className="h-9 flex-1 min-w-[160px] text-sm" placeholder="Etichetta campo" value={c.etichetta}
                        data-testid={`campo-etichetta-${i}`} onChange={(e) => setCampo(i, { etichetta: e.target.value })} />
                      <select className="h-9 text-sm border-2 border-slate-200 rounded-lg px-2 bg-white" value={c.tipo}
                        onChange={(e) => setCampo(i, { tipo: e.target.value })}>
                        {TIPI.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                      {c.tipo === "scelta" && (
                        <Input className="h-9 w-48 text-sm" placeholder="Opzioni separate da ;" value={(c.opzioni || []).join("; ")}
                          onChange={(e) => setCampo(i, { opzioni: e.target.value.split(";").map((s) => s.trim()) })} />
                      )}
                      <label className="flex items-center gap-1 text-xs font-semibold">
                        <input type="checkbox" className="w-4 h-4 accent-brand" checked={c.obbligatorio} onChange={(e) => setCampo(i, { obbligatorio: e.target.checked })} /> Obbl.
                      </label>
                      <button type="button" className="text-red-600" onClick={() => setEditing((e) => ({ ...e, campi: e.campi.filter((_, j) => j !== i) }))}>
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
                <Button size="sm" variant="outline" className="h-9 mt-2" data-testid="campo-aggiungi"
                  onClick={() => setEditing((e) => ({ ...e, campi: [...e.campi, { etichetta: "", tipo: "testo", obbligatorio: false, opzioni: [] }] }))}>
                  <Plus className="w-4 h-4 mr-1" /> Aggiungi campo
                </Button>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Annulla</Button>
            <Button data-testid="modulo-salva" onClick={save} className="bg-brand hover:bg-brand-dark">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter122k68 · Dialog conferma "Segna evasa" con note opzionali */}
      <Dialog open={!!evasaFor} onOpenChange={(o) => !o && !evasaFor?.sending && setEvasaFor(null)}>
        <DialogContent aria-describedby={undefined} className="max-w-md" data-testid="dialog-evasa-compilazione">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-emerald-700" /> Segna attività evasa</DialogTitle>
          </DialogHeader>
          {evasaFor && (
            <div className="space-y-3">
              <div className="text-xs text-slate-600 p-2 rounded bg-slate-50 border border-slate-200">
                <div>Modulo: <b>{evasaFor.compilazione.modulo_titolo}</b></div>
                <div>Condòmino: <b>{evasaFor.compilazione.user_nome}</b> ({evasaFor.compilazione.condominio_nome})</div>
              </div>
              <div>
                <Label className="text-xs font-bold uppercase">Note interne (opzionali)</Label>
                <Input data-testid="evasa-note" value={evasaFor.note}
                  placeholder="Es. Copia chiavi consegnata il 20/02 alle 10:00"
                  onChange={(e) => setEvasaFor((r) => ({ ...r, note: e.target.value }))} className="mt-1" />
                <div className="text-[11px] text-slate-500 mt-1">
                  Le note restano interne allo Studio (non inviate al condòmino).
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" data-testid="evasa-annulla" disabled={evasaFor?.sending}
              onClick={() => setEvasaFor(null)}>Annulla</Button>
            <Button data-testid="evasa-conferma" disabled={evasaFor?.sending}
              onClick={confermaEvasa} className="bg-emerald-700 hover:bg-emerald-800">
              <CheckCircle2 className="w-4 h-4 mr-1" /> {evasaFor?.sending ? "Attendere…" : "Conferma evasione"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
